import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Home, Sparkles, FileText, Briefcase, Users, Star, Settings, LogOut, Shield, UserPlus, X, Bell, Camera } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const sideLinks = [
  { name: "Dashboard", icon: Home, path: "/dashboard" },
  { name: "Generate New Map", icon: Sparkles, path: "/generate" },
  { name: "My Maps", icon: FileText, path: "/dashboard" },
  { name: "Job Descriptions", icon: Briefcase, path: "/dashboard" },
  { name: "Community", icon: Users, path: "/community" },
  { name: "She Leads", icon: Star, path: "/she-leads" },
  { name: "Account Settings", icon: Settings, path: "/settings" },
];

export default function SettingsPage() {
  const { user, loading, logout, updateProfile, changePassword } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("profile");
  const [profile, setProfile] = useState({ name: "", company: "", job_title: "", industry: "", phone: "", gender: "" });
  const [passwords, setPasswords] = useState({ current: "", new_pass: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  // Team state
  const [teamMembers, setTeamMembers] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamMsg, setTeamMsg] = useState("");
  const [teamError, setTeamError] = useState("");
  // Notifications
  const [notifications, setNotifications] = useState([]);

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [user, loading, navigate]);
  useEffect(() => {
    if (user) {
      setProfile({ name: user.name || "", company: user.company || "", job_title: user.job_title || "", industry: user.industry || "", phone: user.phone || "", gender: user.gender || "" });
    }
  }, [user]);

  useEffect(() => {
    if (user && tab === "team" && user.plan === "enterprise") loadTeam();
    if (user && tab === "notifications") loadNotifications();
  }, [user, tab]);

  async function loadTeam() {
    setTeamLoading(true);
    try {
      const res = await axios.get(`${API}/api/team/members`, { withCredentials: true });
      setTeamMembers(res.data);
    } catch { setTeamMembers([]); }
    finally { setTeamLoading(false); }
  }

  async function inviteMember(e) {
    e.preventDefault();
    setTeamMsg(""); setTeamError("");
    if (!inviteEmail.trim()) return;
    try {
      const res = await axios.post(`${API}/api/team/invite`, { email: inviteEmail.trim() }, { withCredentials: true });
      setTeamMsg(res.data.message);
      setInviteEmail("");
      loadTeam();
    } catch (err) {
      setTeamError(err?.response?.data?.detail || "Failed to invite");
    }
  }

  async function removeMember(memberId) {
    if (!window.confirm("Remove this team member? They will be downgraded to Starter plan.")) return;
    try {
      await axios.post(`${API}/api/team/remove/${memberId}`, {}, { withCredentials: true });
      setTeamMembers(prev => prev.filter(m => m._id !== memberId));
      setTeamMsg("Member removed");
    } catch (err) {
      setTeamError(err?.response?.data?.detail || "Failed to remove");
    }
  }

  async function loadNotifications() {
    try {
      const res = await axios.get(`${API}/api/notifications`, { withCredentials: true });
      setNotifications(res.data);
    } catch { setNotifications([]); }
  }

  async function markRead(notifId) {
    try {
      await axios.post(`${API}/api/notifications/${notifId}/read`, {}, { withCredentials: true });
      setNotifications(prev => prev.filter(n => n._id !== notifId));
    } catch (err) { console.error(err); }
  }

  const handleLogout = async () => { await logout(); navigate("/"); };

  const saveProfile = async (e) => {
    e.preventDefault(); setSaving(true); setMsg(""); setError("");
    try { await updateProfile(profile); setMsg("Profile updated!"); } catch { setError("Failed to update"); }
    finally { setSaving(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault(); setMsg(""); setError("");
    if (passwords.new_pass !== passwords.confirm) { setError("Passwords don't match"); return; }
    if (passwords.new_pass.length < 6) { setError("Password must be at least 6 characters"); return; }
    setSaving(true);
    try { await changePassword(passwords.current, passwords.new_pass); setMsg("Password updated!"); setPasswords({ current: "", new_pass: "", confirm: "" }); }
    catch (err) { setError(err?.response?.data?.detail || "Failed to change password"); }
    finally { setSaving(false); }
  };

  if (loading || !user) return <div className="min-h-screen bg-cream flex items-center justify-center"><p className="font-dm text-navy/50">Loading...</p></div>;

  const allTabs = ["profile", "subscription", "security", "notifications"];
  if (user.plan === "enterprise" && !user.team_owner_id) allTabs.splice(2, 0, "team");

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside className="w-64 bg-navy flex-shrink-0 flex flex-col min-h-screen fixed left-0 top-16">
        <div className="p-6">
          <Link to="/" className="inline-block mb-8">
            <span className="font-playfair text-xl"><span className="text-white font-bold">Role</span><span className="text-gold font-bold">IQ</span></span>
          </Link>
          <nav className="space-y-1">
            {sideLinks.map((l) => (
              <Link key={l.name} to={l.path}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg font-dm text-sm transition-colors ${l.path === "/settings" ? "bg-gold/20 text-gold" : "text-white/70 hover:text-white hover:bg-white/5"}`}
              ><l.icon className="w-4 h-4" /> {l.name}</Link>
            ))}
            {user?.role === "admin" && (
              <Link to="/admin" className="flex items-center gap-3 px-4 py-2.5 rounded-lg font-dm text-sm text-red-300 hover:text-red-200 hover:bg-red-500/10 transition-colors">
                <Shield className="w-4 h-4" /> Admin Panel
              </Link>
            )}
          </nav>
        </div>
        <div className="mt-auto p-6 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-gold/20 rounded-full flex items-center justify-center">
              <span className="font-dm text-gold text-sm font-bold">{user.name?.[0]?.toUpperCase()}</span>
            </div>
            <div>
              <p className="font-dm text-white text-sm font-semibold truncate max-w-[120px]">{user.name}</p>
              <span className="inline-block bg-gold/20 text-gold font-dm text-[10px] px-2 py-0.5 rounded-full capitalize">{user.plan}</span>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-white/50 hover:text-white font-dm text-sm"><LogOut className="w-4 h-4" /> Logout</button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 ml-64 bg-cream min-h-screen pt-16">
        <div className="bg-white px-8 py-5 border-b border-navy/5">
          <h1 className="font-playfair text-navy text-2xl">Account Settings</h1>
        </div>
        <div className="p-8">
          {/* Tabs */}
          <div className="flex gap-1 bg-white rounded-lg p-1 w-fit shadow-sm mb-8 flex-wrap">
            {allTabs.map((t) => (
              <button key={t} onClick={() => { setTab(t); setMsg(""); setError(""); setTeamMsg(""); setTeamError(""); }}
                className={`font-dm text-sm px-5 py-2 rounded-lg transition-colors capitalize ${tab === t ? "bg-navy text-white" : "text-navy/50 hover:text-navy"}`}
              >{t}</button>
            ))}
          </div>

          {msg && <div className="bg-green-50 border border-green-200 text-green-700 font-dm text-sm px-4 py-3 rounded-lg mb-4">{msg}</div>}
          {error && <div className="bg-red-50 border border-red-200 text-red-700 font-dm text-sm px-4 py-3 rounded-lg mb-4">{error}</div>}

          {/* Profile Tab */}
          {tab === "profile" && (
            <form onSubmit={saveProfile} className="bg-white rounded-xl shadow-md p-8 max-w-lg space-y-4">
              {/* Avatar Upload */}
              <div className="flex items-center gap-5 mb-2">
                <div className="relative">
                  {(user.avatar_url || user.picture) ? (
                    <img src={`${user.avatar_url?.startsWith("/api") ? API : ""}${user.avatar_url || user.picture}`} alt="" className="w-20 h-20 rounded-full object-cover border-3 border-gold" />
                  ) : (
                    <div className="w-20 h-20 bg-gold/10 rounded-full flex items-center justify-center border-3 border-gold">
                      <span className="font-playfair text-gold text-2xl font-bold">{user.name?.[0]?.toUpperCase()}</span>
                    </div>
                  )}
                  <label className="absolute -bottom-1 -right-1 w-8 h-8 bg-navy rounded-full flex items-center justify-center cursor-pointer hover:bg-gold transition-colors shadow-lg" data-testid="avatar-upload-btn">
                    <Camera className="w-4 h-4 text-white" />
                    <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const formData = new FormData();
                      formData.append("file", file);
                      try {
                        const res = await axios.post(`${API}/api/auth/avatar`, formData, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" }});
                        setMsg("Profile photo updated!");
                        window.location.reload();
                      } catch (err) {
                        setError(err?.response?.data?.detail || "Failed to upload photo");
                      }
                    }} />
                  </label>
                </div>
                <div>
                  <p className="font-dm text-navy text-sm font-semibold">{user.name}</p>
                  <p className="font-dm text-navy/50 text-xs">{user.email}</p>
                  <p className="font-dm text-navy/40 text-xs mt-1">Click camera icon to change photo</p>
                </div>
              </div>
              <div><label className="font-dm text-navy text-sm font-semibold">Full Name</label>
                <input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  className="w-full mt-1 border border-navy/20 rounded-lg px-4 py-2.5 font-dm text-sm focus:border-gold focus:outline-none" /></div>
              <div><label className="font-dm text-navy text-sm font-semibold">Job Title</label>
                <input value={profile.job_title} onChange={(e) => setProfile({ ...profile, job_title: e.target.value })}
                  className="w-full mt-1 border border-navy/20 rounded-lg px-4 py-2.5 font-dm text-sm focus:border-gold focus:outline-none" /></div>
              <div><label className="font-dm text-navy text-sm font-semibold">Company</label>
                <input value={profile.company} onChange={(e) => setProfile({ ...profile, company: e.target.value })}
                  className="w-full mt-1 border border-navy/20 rounded-lg px-4 py-2.5 font-dm text-sm focus:border-gold focus:outline-none" /></div>
              <div><label className="font-dm text-navy text-sm font-semibold">Industry</label>
                <input value={profile.industry} onChange={(e) => setProfile({ ...profile, industry: e.target.value })}
                  className="w-full mt-1 border border-navy/20 rounded-lg px-4 py-2.5 font-dm text-sm focus:border-gold focus:outline-none" /></div>
              <div><label className="font-dm text-navy text-sm font-semibold">Phone</label>
                <input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  className="w-full mt-1 border border-navy/20 rounded-lg px-4 py-2.5 font-dm text-sm focus:border-gold focus:outline-none" /></div>
              <div><label className="font-dm text-navy text-sm font-semibold">Gender</label>
                {user.gender ? (
                  <>
                    <input value={user.gender.charAt(0).toUpperCase() + user.gender.slice(1)} disabled readOnly data-testid="settings-gender-locked"
                      className="w-full mt-1 border border-navy/20 rounded-lg px-4 py-2.5 font-dm text-sm bg-navy/5 text-navy/60 cursor-not-allowed" />
                    <p className="font-dm text-navy/50 text-xs mt-1.5 italic">Gender cannot be changed after registration. Contact <a href="mailto:hello@roleiq.pro" className="text-gold hover:underline">hello@roleiq.pro</a> if there is an error.</p>
                  </>
                ) : (
                  <>
                    <select value={profile.gender} onChange={(e) => setProfile({ ...profile, gender: e.target.value })} data-testid="settings-gender"
                      className="w-full mt-1 border border-navy/20 rounded-lg px-4 py-2.5 font-dm text-sm focus:border-gold focus:outline-none">
                      <option value="">Prefer not to say</option>
                      <option value="female">Female</option>
                      <option value="male">Male</option>
                      <option value="other">Other</option>
                    </select>
                    <p className="font-dm text-orange-600 text-xs mt-1.5">Once saved, your gender cannot be changed.</p>
                  </>
                )}
              </div>
              <button type="submit" disabled={saving} className="bg-navy text-white font-dm font-bold px-6 py-2.5 rounded-lg hover:bg-gold hover:text-navy transition-colors disabled:opacity-50">
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </form>
          )}

          {/* Subscription Tab */}
          {tab === "subscription" && (
            <div className="bg-white rounded-xl shadow-md p-8 max-w-lg">
              <h3 className="font-playfair text-navy text-xl font-semibold mb-4">Current Plan</h3>
              <div className="border border-gold/20 rounded-xl p-5 mb-6">
                <span className="inline-block bg-gold/10 text-gold font-dm text-sm font-bold px-3 py-1 rounded-full capitalize mb-2">{user.plan}</span>
                <p className="font-dm text-navy/50 text-sm">Your plan renews monthly. Manage your subscription anytime.</p>
              </div>
              {user.plan === "starter" && (
                <Link to="/pricing" className="inline-block bg-gold text-navy font-dm font-bold px-6 py-2.5 rounded-lg hover:bg-gold/90 transition-colors">
                  Upgrade Plan
                </Link>
              )}
            </div>
          )}

          {/* Team Tab (Enterprise only) */}
          {tab === "team" && user.plan === "enterprise" && (
            <div className="max-w-lg space-y-6">
              <div className="bg-white rounded-xl shadow-md p-8">
                <h3 className="font-playfair text-navy text-xl font-semibold mb-4">Team Management</h3>
                <p className="font-dm text-navy/50 text-sm mb-6">Invite up to 10 team members. They'll get Enterprise access automatically.</p>
                {teamMsg && <div className="bg-green-50 border border-green-200 text-green-700 font-dm text-sm px-4 py-3 rounded-lg mb-4">{teamMsg}</div>}
                {teamError && <div className="bg-red-50 border border-red-200 text-red-700 font-dm text-sm px-4 py-3 rounded-lg mb-4">{teamError}</div>}
                <form onSubmit={inviteMember} className="flex gap-2 mb-6">
                  <input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} type="email" placeholder="team@company.com" data-testid="team-invite-email"
                    className="flex-1 border border-navy/20 rounded-lg px-4 py-2.5 font-dm text-sm focus:border-gold focus:outline-none" />
                  <button type="submit" data-testid="team-invite-btn"
                    className="bg-gold text-navy font-dm font-bold px-5 py-2.5 rounded-lg hover:bg-gold/90 transition-colors flex items-center gap-2">
                    <UserPlus className="w-4 h-4" /> Invite
                  </button>
                </form>
                <div className="space-y-3">
                  {teamLoading ? (
                    <p className="font-dm text-navy/40 text-sm">Loading team...</p>
                  ) : teamMembers.length === 0 ? (
                    <p className="font-dm text-navy/40 text-sm">No team members yet</p>
                  ) : (
                    teamMembers.map((m) => (
                      <div key={m._id} className="flex items-center justify-between border border-navy/10 rounded-lg p-3">
                        <div>
                          <p className="font-dm text-navy text-sm font-semibold">{m.name}</p>
                          <p className="font-dm text-navy/50 text-xs">{m.email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`font-dm text-xs px-2 py-0.5 rounded-full ${m.team_owner_id ? "bg-navy/5 text-navy/50" : "bg-gold/10 text-gold font-semibold"}`}>
                            {m.team_owner_id ? "Member" : "Owner"}
                          </span>
                          {m.team_owner_id && m._id !== user.id && (
                            <button onClick={() => removeMember(m._id)} className="text-red-400 hover:text-red-600 transition-colors" title="Remove">
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Security Tab */}
          {tab === "security" && (
            <div className="space-y-6 max-w-lg">
              <form onSubmit={savePassword} className="bg-white rounded-xl shadow-md p-8 space-y-4">
                <h3 className="font-playfair text-navy text-xl font-semibold">Change Password</h3>
                <input type="password" required placeholder="Current Password" value={passwords.current} onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                  className="w-full border border-navy/20 rounded-lg px-4 py-2.5 font-dm text-sm focus:border-gold focus:outline-none" />
                <input type="password" required placeholder="New Password" value={passwords.new_pass} onChange={(e) => setPasswords({ ...passwords, new_pass: e.target.value })}
                  className="w-full border border-navy/20 rounded-lg px-4 py-2.5 font-dm text-sm focus:border-gold focus:outline-none" />
                <input type="password" required placeholder="Confirm New Password" value={passwords.confirm} onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                  className="w-full border border-navy/20 rounded-lg px-4 py-2.5 font-dm text-sm focus:border-gold focus:outline-none" />
                <button type="submit" disabled={saving} className="bg-navy text-white font-dm font-bold px-6 py-2.5 rounded-lg hover:bg-gold hover:text-navy transition-colors disabled:opacity-50">
                  {saving ? "Updating..." : "Update Password"}
                </button>
              </form>
              <div className="bg-white rounded-xl shadow-md p-8 border border-red-100">
                <h3 className="font-dm text-red-600 font-semibold mb-1">Danger Zone</h3>
                <p className="font-dm text-navy/50 text-sm mb-3">Once you delete your account, there is no going back.</p>
                <button className="border border-red-300 text-red-500 font-dm text-sm px-5 py-2 rounded-lg hover:bg-red-50 transition-colors">Delete Account</button>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {tab === "notifications" && (
            <div className="max-w-lg">
              <div className="bg-white rounded-xl shadow-md p-8">
                <h3 className="font-playfair text-navy text-xl font-semibold mb-4 flex items-center gap-2">
                  <Bell className="w-5 h-5 text-gold" /> Notifications
                </h3>
                {notifications.length === 0 ? (
                  <p className="font-dm text-navy/40 text-sm py-6 text-center">No new notifications</p>
                ) : (
                  <div className="space-y-3">
                    {notifications.map((n) => (
                      <div key={n._id} className="flex items-start justify-between border border-navy/10 rounded-lg p-4">
                        <div>
                          <p className="font-dm text-navy text-sm font-semibold">{n.title}</p>
                          <p className="font-dm text-navy/60 text-xs mt-0.5">{n.message}</p>
                          <p className="font-dm text-navy/30 text-[10px] mt-1">{new Date(n.created_at).toLocaleString("en-IN")}</p>
                        </div>
                        <button onClick={() => markRead(n._id)} className="text-navy/30 hover:text-navy transition-colors flex-shrink-0" title="Mark as read">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
